import { css } from "@emotion/css";
import { ActionButton, Callout, Divider } from "@octopusdeploy/design-system-components";
import { CheckThickIcon, XmarkIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ApprovalResource, UserResource, GetServerTaskApprovalByTaskIdResponseResource, ApprovalStatus } from "@octopusdeploy/octopus-server-client";
import { OctopusError, Permission } from "@octopusdeploy/octopus-server-client";
import { useEffect, useState } from "react";
import { VerticalDivider } from "~/areas/tenants/components/HeaderBar/CollapsibleFilter";
import { session } from "~/clientInstance";
import Chip from "~/components/Chips/Chip";
import { UserChip } from "~/components/Chips/UserChip";
import { TeamChip } from "~/components/Chips/index";
import ErrorPanel from "~/components/ErrorPanel/index";
import { Section } from "~/components/Section/Section";
import { Text } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
export interface ApprovalsInnerProps {
    approvalPolicyResponse: GetServerTaskApprovalByTaskIdResponseResource;
    teamMembershipIds: string[];
    serverTaskState: string | undefined;
    users: UserResource[] | undefined;
    refetch: () => void;
}
export const ApprovalsInner = ({ approvalPolicyResponse, teamMembershipIds, serverTaskState, refetch, users }: ApprovalsInnerProps) => {
    const currentUsersApproval = approvalPolicyResponse.Approvals.find((a) => a.UserId === session.currentUser?.Id);
    const [status, setStatus] = useState<ApprovalStatus>();
    const [error, setError] = useState<Error | null>(null);
    const { model, setModel, formProps, submit } = useForm({
        formName: "Task Approvals",
        initialModel: currentUsersApproval || { ApprovalStatus: undefined, Id: "", Name: "", Notes: "", ServerTaskApprovalId: approvalPolicyResponse.Id, SpaceId: approvalPolicyResponse.SpaceId, UserId: session.currentUser?.Id },
        onSubmit: (repository, model) => repository.Approvals.create(approvalPolicyResponse.Id, {
            Notes: model.Notes,
            ServerTaskApprovalId: approvalPolicyResponse.Id,
            SpaceId: approvalPolicyResponse.SpaceId,
            Status: status,
        }),
        updateFormModelAfterSubmit: (model) => model,
        submitPermission: { permission: Permission.AdministerSystem },
        snackbarSaveText: "Approval submitted",
        onSubmitError: (error) => {
            setError(error);
            setStatus(undefined);
        },
        afterSubmit: async (_, updatedModel) => {
            refetch();
            setError(null);
        },
    });
    useEffect(() => {
        if (status) {
            setModel({ ...model, Status: status });
            submit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);
    const userCanSubmit = () => {
        if (currentUsersApproval)
            return false;
        if (approvalPolicyResponse.ApprovingUsers.find((u) => u.Id === session.currentUser?.Id))
            return true;
        return !!approvalPolicyResponse.ApprovingTeams.find((t) => teamMembershipIds?.includes(t.Id));
    };
    const minimumApprovers = approvalPolicyResponse?.MinimumApproversRequired;
    const approvalsRequired = `${minimumApprovers - approvalPolicyResponse?.ApprovalsCount}/${minimumApprovers} Approvals Required`;
    const renderSubmitted = (approval: ApprovalResource) => (<div>
            <div className={styles.header}>
                <div className={styles.icon}>{approval.Status === "Approved" ? <CheckThickIcon size={24} color={themeTokens.color.icon.success}/> : <XmarkIcon size={24} color={themeTokens.color.icon.danger}/>}</div>
                <h4>{approval.Status === "Approved" ? "You have approved this deployment" : "You have rejected this deployment"}</h4>
            </div>
            <h4>Notes:</h4>
            <p>{approval.Notes}</p>
        </div>);
    const renderSubmittedListItem = (approval: ApprovalResource) => {
        const user = users?.find((u) => u.Id === approval.UserId);
        return (<div>
                <div className={styles.header}>
                    <div className={styles.icon}>{approval.Status === "Approved" ? <CheckThickIcon size={20} color={themeTokens.color.icon.success}/> : <XmarkIcon size={24} color={themeTokens.color.icon.danger}/>}</div>
                    <div className={styles.chip}>{user && <UserChip user={user}></UserChip>}</div>
                    <p>{approval.Notes}</p>
                </div>
            </div>);
    };
    const renderCreateApprovalForm = () => (<>
            <Form {...formProps}>
                <h4>Notes</h4>
                <Text value={model?.Notes} multiline={true} onChange={(Notes) => setModel((prev) => ({ ...prev, Notes }))}/>
                <div className={styles.actionButtons}>
                    <ActionButton label={"Approve"} onClick={() => setStatus("Approved")}/>
                    <ActionButton label={"Reject"} onClick={() => setStatus("Rejected")}/>
                </div>
            </Form>
        </>);
    const isTaskStatePreExecution = serverTaskState === "Queued" || serverTaskState === "New";
    return (<div>
            {error && <ErrorPanel message={OctopusError.isOctopusError(error) ? error.ErrorMessage : error.message} errors={OctopusError.isOctopusError(error) ? error.Errors : undefined} scrollToPanel={false}/>}

            <Section>
                {isTaskStatePreExecution && approvalPolicyResponse.ApprovalState === "PreApproval" && <Callout type={"warning"} title={approvalsRequired}/>}
                {approvalPolicyResponse.ApprovalState === "Approved" && <Callout type={"success"} title={"Deployment Approved"}/>}

                <div className={styles.approvalsContent}>
                    <div className={styles.formContainer}>
                        {isTaskStatePreExecution && approvalPolicyResponse.ApprovalState === "PreApproval" && userCanSubmit() && renderCreateApprovalForm()}
                        {currentUsersApproval && renderSubmitted(currentUsersApproval)}

                        {currentUsersApproval && <Divider />}
                        <h4>
                            {approvalPolicyResponse.ApprovalsCount} Existing Approval{approvalPolicyResponse.ApprovalsCount === 1 ? "" : "s"}
                        </h4>
                        {approvalPolicyResponse?.Approvals.map((a) => renderSubmittedListItem(a))}
                    </div>

                    <VerticalDivider />
                    <div className={styles.approversContainer}>
                        <h4>Authorized Approvers</h4>
                        <div className={styles.approversChips}>
                            {approvalPolicyResponse?.ApprovingUsers.map((u) => (<Chip description={`User: ${u.DisplayName}`} key={u.Id}>
                                    {u.DisplayName}
                                </Chip>))}
                            {approvalPolicyResponse?.ApprovingTeams.map((t) => (<TeamChip key={t.Id} team={t}/>))}
                        </div>
                    </div>
                </div>
            </Section>
        </div>);
};
const styles = {
    approvalsContent: css({
        display: "flex",
    }),
    formContainer: css({
        width: "85%",
        marginRight: space[12],
    }),
    header: css({
        display: "flex",
    }),
    icon: css({
        marginTop: space[12],
        marginRight: space[12],
    }),
    approversContainer: css({
        marginLeft: space[24],
    }),
    approversChips: css({
        marginTop: space[24],
    }),
    actionButtons: css({
        display: "flex",
        justifyContent: "flex-end",
        gap: space[12],
        marginBottom: space[24],
    }),
    chip: css({
        marginTop: space[8],
        marginRight: space[8],
    }),
};
